<template>
  <div class="sy-pagination" ref="paginationDom">
    <!-- 加载中状态：紧凑的底部加载条 -->
    <div v-if="hasMore" class="loading-indicator">
      <div class="loading-spinner"></div>
      <span class="loading-text">{{ showText }}</span>
    </div>
    <!-- 已到底状态 -->
    <div v-else class="no-more-text">
      <span>{{ showText }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'pagination',
  props: {
    pageNo: {
      type: Number,
      default: 0
    },
    pages: {
      type: Number,
      default: 0
    },
    /**
     * 父组件的请求状态。
     * 之前只靠「pageNo 变化」来解锁内部的 isLoading，一旦某次加载失败（pageNo 不变）
     * 或服务端返回了同一个 current，锁就永远解不开，无限滚动彻底失效且底部一直转圈。
     * 现在把父组件的 in-flight 状态透传进来：请求结束（无论成功失败）即解锁。
     */
    loading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isLoading: false
    }
  },
  computed: {
    hasMore () {
      return this.pageNo < this.pages
    },
    showText () {
      return this.hasMore ? '正在加载中...' : '已经到底了~'
    }
  },
  watch: {
    // pageNo 变化说明上一次加载完成，解锁
    pageNo () {
      this.isLoading = false
      // 数据变更后重新挂载 observer（因为新的数据可能改变了滚动位置）
      this.$nextTick(() => {
        this.initObserver()
      })
    },
    // 父组件请求结束（含失败）时解锁，避免一次失败后再也无法加载更多
    loading (val) {
      if (val) return
      this.isLoading = false
      this.$nextTick(() => {
        this.initObserver()
      })
    },
    // hasMore 变化时也需要重建 observer
    hasMore (val) {
      if (val) {
        this.$nextTick(() => {
          this.initObserver()
        })
      } else {
        this.observer?.disconnect()
      }
    }
  },
  mounted () {
    this.initObserver()
  },
  beforeDestroy () {
    this.observer?.disconnect()
  },
  methods: {
    initObserver () {
      this.observer?.disconnect()
      const observeDom = this.$refs.paginationDom
      if (!observeDom || !this.hasMore) return

      this.observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting && this.hasMore && !this.isLoading && !this.loading) {
          this.isLoading = true
          this.$emit('pageChange', { pageNo: this.pageNo + 1 })
        }
      }, {
        // 提前 600px 触发加载，让数据在用户滚到底之前就准备好
        rootMargin: '0px 0px 600px 0px',
        threshold: 0.01
      })

      this.$nextTick(() => {
        if (this.$refs.paginationDom) {
          this.observer.observe(this.$refs.paginationDom)
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.sy-pagination {
  width: 100%;
  padding: 20px 0 32px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.loading-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px 0;
  animation: fadeIn 0.2s ease;
}

.loading-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid #e4e7ed;
  border-top-color: #409eff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.loading-text {
  font-size: 13px;
  color: #909399;
  letter-spacing: 0.5px;
}

.no-more-text {
  text-align: center;
  padding: 12px 0;
  color: #c0c4cc;
  font-size: 13px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
